import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Popover,
  IconButton,
  Fade,
  Stack,
  Chip
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { PickersDay } from '@mui/x-date-pickers';
import { useSelector } from 'react-redux';
import { brandingInfo } from 'features/server/brandingSlice';
import { themes } from 'theme/themes';

interface DateRangePickerProps {
    label?: string;
    value: [Dayjs | null, Dayjs | null];
    onChange: (value: [Dayjs | null, Dayjs | null]) => void;
    error?: string | undefined;
    controlledOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    placeholder?: string;
    maxDate?: Dayjs;
    shortcuts?: { label: string, getValue: () => any[] }[];
    disableDateRange?: boolean;
}
const DateRangePicker: React.FC<DateRangePickerProps> = ({
    label, value, onChange, error,
    controlledOpen, onOpenChange, placeholder, maxDate, shortcuts, disableDateRange
}) => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
    const [internalAnchor, setInternalAnchor] = useState<HTMLElement | null>(null);
    const [activeShortcut, setActiveShortcut] = useState<string | null>(null);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : Boolean(internalAnchor);
    const setOpen = (state: boolean, anchor?: HTMLElement | null) => {
        if (isControlled) onOpenChange?.(state);
        else setInternalAnchor(state ? anchor || internalAnchor : null);
    };
    const startDate = value?.[0];
    const endDate = value?.[1];
    const displayValue =
        startDate && endDate ? `${startDate.format('YYYY-MM-DD')} → ${endDate.format('YYYY-MM-DD')}`
        : startDate ? `${startDate.format('YYYY-MM-DD')} → YYYY-MM-DD`
        : endDate ? `YYYY-MM-DD → ${endDate.format('YYYY-MM-DD')}`
        : '';
    const handleDateSelect = (selectedDate: Dayjs) => {
        setActiveShortcut(t('customDateRange'));
        if (!startDate || (startDate && endDate)) {
            onChange([selectedDate, null]);
        } else {
            if (selectedDate.isBefore(startDate)) {
                onChange([selectedDate, startDate]);
            } else {
                onChange([startDate, selectedDate]);
            }
            setTimeout(() => setOpen(false), 100);
        }
    };
    const handleShortcutClick = (shortcut: any) => {
        const [start, end] = shortcut.getValue();
        setActiveShortcut(shortcut.label);
        onChange([start, end]);
        if (start) {
            setCurrentMonth(start.startOf('month'));
        }
        if (shortcut.label !== t('customDateRange')) {
            setTimeout(() => setOpen(false), 100);
        }
    };
    const rangeError = (startDate && !endDate) || (!startDate && endDate);
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
            <Box id="form">
                <TextField 
                    label={label}
                    fullWidth
                    error={!!error || rangeError as any} 
                    helperText={error && error}
                    placeholder={placeholder} 
                    onClick={(e) => setOpen(true, e.currentTarget)}
                    InputProps={{
                        readOnly: true,
                        endAdornment: displayValue && (
                            <IconButton size="small" onClick={(e) => {
                                e.stopPropagation();
                                onChange([null, null]);
                                setActiveShortcut(null);
                            }} className='clearButton'><ClearIcon fontSize="small" /></IconButton>
                        )
                    }}
                    InputLabelProps={{ shrink: true }}                              
                    sx={{ '& .MuiOutlinedInput-root': { paddingRight: '0px !important' }, minWidth: '220px' }}
                    value={displayValue}
                    disabled={disableDateRange}
                />
                <Popover
                    open={open}
                    anchorEl={internalAnchor}
                    onClose={() => setOpen(false)}
                    TransitionComponent={Fade}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    disableScrollLock
                    sx={{ mt: '1px' }}
                >
                    <Box>
                        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' }, }}>
                            {shortcuts ? <Stack direction="column" spacing={1} 
                                sx={{ borderRight: '1px solid rgba(0, 0, 0, 0.12)', p: 3, maxHeight: { xs: 'none', sm: '334px' } }}
                                className='grid overflow-y-auto'
                            >
                                {shortcuts?.map((shortcut: any, index: number) => (
                                    <Chip 
                                        key={`shortcut-${index}`}
                                        label={shortcut.label} 
                                        clickable
                                        onClick={() => handleShortcutClick(shortcut)}
                                        sx={{ width: 'fit-content', 
                                            color: activeShortcut === shortcut.label ? themeColors?.primary : 'inherit',
                                            outline: activeShortcut === shortcut.label ? `1px solid ${themeColors?.primary}` : 'none'
                                        }}
                                    />
                                ))}
                            </Stack> : null}
                            <DateCalendar
                                value={startDate ?? null}
                                onChange={handleDateSelect}
                                referenceDate={currentMonth}
                                onMonthChange={(newMonth) => setCurrentMonth(newMonth)}
                                sx={{ borderRight: '1px solid rgba(0, 0, 0, 0.12)', pt: { xs: 0, sm: 1 } }}
                                maxDate={maxDate}
                                slots={{
                                    day: (props) => {
                                        const { day, ...other } = props;
                                        const isStart = startDate && day.isSame(startDate, 'day');
                                        const isEnd = endDate && day.isSame(endDate, 'day');
                                        const isInRange = startDate && endDate && day.isAfter(startDate, 'day') && day.isBefore(endDate, 'day');
                                        return (
                                            <PickersDay
                                                {...other}
                                                day={day}
                                                sx={{
                                                    ...(isInRange && { backgroundColor: '#DBEAF8', borderRadius: 0, margin: 0 }),
                                                    ...(isStart && { backgroundColor: 'primary.main', color: '#fff', borderRadius: '50% 0 0 50%', margin: 0 }),
                                                    ...(isEnd && { backgroundColor: 'primary.main', color: '#fff', borderRadius: '0 50% 50% 0', margin: 0 }),
                                                    ...(isStart && isEnd && { borderRadius: '50%' })
                                                }}
                                            />
                                        );
                                    },
                                }}
                            />
                            <DateCalendar
                                value={startDate && endDate && !startDate.isSame(endDate, 'month') ? endDate : null}
                                onChange={handleDateSelect}
                                referenceDate={currentMonth.add(1, 'month')}
                                onMonthChange={(newMonth) => setCurrentMonth(newMonth.subtract(1, 'month'))}
                                sx={{ pt: { xs: 0, sm: 1 } }}
                                maxDate={maxDate}
                                slots={{
                                    day: (props) => {
                                        const { day, ...other } = props;
                                        const isStart = startDate && day.isSame(startDate, 'day');
                                        const isEnd = endDate && day.isSame(endDate, 'day');
                                        const isInRange = startDate && endDate && day.isAfter(startDate, 'day') && day.isBefore(endDate, 'day');
                                        return (
                                            <PickersDay
                                                {...other}
                                                day={day}
                                                sx={{
                                                    ...(isInRange && { backgroundColor: '#DBEAF8', borderRadius: 0, margin: 0 }),
                                                    ...(isStart && { backgroundColor: 'primary.main', color: '#fff', borderRadius: '50% 0 0 50%', margin: 0 }),
                                                    ...(isEnd && { backgroundColor: 'primary.main', color: '#fff', borderRadius: '0 50% 50% 0', margin: 0 }),
                                                    ...(isStart && isEnd && { borderRadius: '50%' })
                                                }}
                                            />
                                        );
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </Popover>
            </Box>
        </LocalizationProvider>
    );
};
export default DateRangePicker;